import React from 'react';
import {
  Paper,
  Typography,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BookIcon from '@mui/icons-material/Book';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DateRangeIcon from '@mui/icons-material/DateRange';
import MenuIcon from '@mui/icons-material/Menu';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/**
 * MobileHeader Component
 * Renders the header for mobile interface
 */
const MobileHeader = ({ user, chatPhase, initialLoading }) => {
  if (chatPhase !== 'main' || initialLoading) {
    return null;
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Typography variant="h6">AIMHI</Typography>
      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
        {user?.email}
      </Typography>
    </Paper>
  );
};

/**
 * MobileFeatureMenu Component
 * Renders the floating action button menu for mobile
 */
const MobileFeatureMenu = ({
  chatPhase,
  initialLoading,
  currentConversationId,
  sessionsEnabled,
  clientTherapySettings,
  handlers = {},
  reviewRequestStatus = {}
}) => {
  if (chatPhase !== 'main' || initialLoading) {
    return null;
  }

  const {
    handleOpenGoalModal,
    handleOpenSessionHistoryModal,
    handleOpenJournalModal,
    handleOpenCheckInModal,
    handleOpenCheckInHistoryModal,
    handleOpenSessionReviewModal,
    handleOpenProgressModal,
    handleRequestReview,
    handleOpenSupportTicket
  } = handlers;

  // Build actions array
  const actions = [
    { 
      icon: <TrackChangesIcon />, 
      name: 'Goals', 
      action: handleOpenGoalModal 
    },
    { 
      icon: <BookIcon />, 
      name: 'Journal', 
      action: handleOpenJournalModal 
    },
    { 
      icon: <EventNoteIcon />, 
      name: 'Check-In', 
      action: handleOpenCheckInModal 
    },
    { 
      icon: <HistoryIcon />, 
      name: 'Check-In History', 
      action: handleOpenCheckInHistoryModal 
    },
    { 
      icon: <ReceiptLongIcon />, 
      name: 'Session Review', 
      action: handleOpenSessionReviewModal, 
      disabled: !currentConversationId 
    },
    { 
      icon: <AssessmentIcon />, 
      name: 'Progress', 
      action: handleOpenProgressModal 
    },
    {
      icon: <SupervisorAccountIcon />,
      name: 'Request Review',
      action: handleRequestReview,
      disabled: !currentConversationId || reviewRequestStatus.loading
    },
    {
      icon: <HelpOutlineIcon />,
      name: 'Get Help',
      action: handleOpenSupportTicket
    }
  ];

  // Add session history if sessions are enabled
  if (sessionsEnabled || 
      clientTherapySettings?.sessionMode === 'structured' || 
      clientTherapySettings?.sessionMode === 'hybrid') {
    actions.splice(1, 0, { 
      icon: <DateRangeIcon />, 
      name: 'Session History', 
      action: handleOpenSessionHistoryModal, 
      disabled: !currentConversationId 
    });
  }

  return (
    <SpeedDial
      ariaLabel="Mobile Feature Menu"
      sx={{ position: 'fixed', bottom: 16, right: 16 }}
      icon={<SpeedDialIcon icon={<MenuIcon />} />}
    >
      {actions.map((action) => (
        <SpeedDialAction
          key={action.name}
          icon={action.icon}
          tooltipTitle={action.name}
          onClick={action.action}
          disabled={action.disabled}
        />
      ))}
    </SpeedDial>
  );
};

/**
 * MobileInterface Component
 * Container component that manages both mobile header and feature menu
 * 
 * @param {Object} props - Component props
 * @param {Object} props.user - User object
 * @param {string} props.chatPhase - Current chat phase
 * @param {boolean} props.initialLoading - Whether initial loading is in progress
 * @param {string} props.currentConversationId - Current conversation ID
 * @param {boolean} props.sessionsEnabled - Whether sessions are enabled
 * @param {Object} props.clientTherapySettings - Client therapy settings
 * @param {Object} props.handlers - Object containing all event handlers
 * @param {Object} props.reviewRequestStatus - Review request status object
 * @param {boolean} props.showHeader - Whether to show the mobile header
 * @param {boolean} props.showFeatureMenu - Whether to show the feature menu
 * @returns {JSX.Element} MobileInterface component
 */
const MobileInterface = ({
  user,
  chatPhase,
  initialLoading,
  currentConversationId,
  sessionsEnabled,
  clientTherapySettings,
  handlers,
  reviewRequestStatus,
  showHeader = true,
  showFeatureMenu = true
}) => {
  return (
    <>
      {showHeader && (
        <MobileHeader 
          user={user}
          chatPhase={chatPhase}
          initialLoading={initialLoading}
        />
      )}
      
      {showFeatureMenu && (
        <MobileFeatureMenu
          chatPhase={chatPhase}
          initialLoading={initialLoading}
          currentConversationId={currentConversationId}
          sessionsEnabled={sessionsEnabled}
          clientTherapySettings={clientTherapySettings}
          handlers={handlers}
          reviewRequestStatus={reviewRequestStatus}
        />
      )}
    </>
  );
};

export default MobileInterface;
export { MobileHeader, MobileFeatureMenu };